import React, { useCallback, useEffect, useState } from "react";
import {
    Pagination, PaginationItem, PaginationLink,
    Row,
    Col,
} from "reactstrap";
import { fetchBranches, getCancelledOrderListing, getOrderListing } from "../../utils/apiCalls";
import Loader from "../../shared/Loader";
import OrderDetailModal from "./OrderDetailModal";
import { useSelector } from "react-redux";

const DeliveryCancelledOrderTable = () => {
    const userType = localStorage.getItem("userType");
    const merchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [orderData, setOrderData] = useState([]);
    const [branches, setBranches] = useState([]);
    const [displayedBranches, setDisplayedBranches] = useState([]);
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filters
    const [filters, setFilters] = useState({
        merchant: selectedMerchant || "",
        branch: "",
        limit: 10,
    });

    const getBranches = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetchBranches();
            setBranches(response.data);
            setDisplayedBranches(response.data); // Initially display all branches
        } catch (error) {
            console.error("Error fetching branches:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchDataAndSetRows = useCallback(async (page = 1) => {
        setIsLoading(true);
        const ResData = await getCancelledOrderListing(page, filters.limit, filters);
        if (ResData && ResData.data) {
            setOrderData(ResData.data.cancelledOrders || []);
            setTotalPages(ResData.data.pagination?.totalPages || 1);
        } else {
            setOrderData([]);
            setTotalPages(1);
        }
        setIsLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchDataAndSetRows(currentPage);
    }, [fetchDataAndSetRows, currentPage]);

    useEffect(() => {
        getBranches();
    }, [getBranches]);

    useEffect(() => {
        setFilters(prev => ({ ...prev, merchant: selectedMerchant || "", branch: "" }));
        setCurrentPage(1);
        if (selectedMerchant === "all" || !selectedMerchant) {
            setDisplayedBranches(branches); // Display all branches
        } else {
            const filteredBranches = branches?.filter(branch => branch?.MerchantId == selectedMerchant && branch?.active);
            setDisplayedBranches(filteredBranches); // Display filtered branches
        }
    }, [selectedMerchant, branches]);

    const handleBranchChange = (e) => {
        const branchId = e.target.value;
        setFilters({ ...filters, branch: branchId });
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleOrderClick = async (orderId) => {
        const ResData = await getOrderListing(1, 10, {
            orderId: orderId,
        });
        if (ResData?.data?.orders?.length > 0) {
            setSelectedOrder(ResData?.data?.orders[0]);
            setOrderModalOpen(true);
        }
    }

    const renderPagination = () => {
        const pages = [];
        const maxPagesToShow = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (startPage > 1) {
            pages.push(
                <PaginationItem key="first">
                    <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
                </PaginationItem>
            );
            if (startPage > 2) {
                pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink>...</PaginationLink></PaginationItem>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink>...</PaginationLink></PaginationItem>);
            }
            pages.push(
                <PaginationItem key="last">
                    <PaginationLink onClick={() => handlePageChange(totalPages)}>{totalPages}</PaginationLink>
                </PaginationItem>
            );
        }
        return pages;
    };

    return (
        <>
            {isLoading ? (
                <Loader />
            ) : (
                <div>
                    <Row className="mb-3">
                        <Col className="d-flex justify-content-end align-items-center gap-3">

                            {((filters.merchant && filters.merchant !== 'all') || userType === "merchant") ? (
                                <select
                                    value={filters.branch}
                                    onChange={handleBranchChange}
                                    style={{
                                        padding: "10px",
                                        fontSize: "16px",
                                        borderRadius: "5px",
                                        border: "1px solid #009689",
                                        float: "right",
                                    }}
                                >
                                    <option value="">All Branches</option>
                                    {displayedBranches?.map((branch) => (
                                        <option key={branch.id} value={branch.id}>
                                            {branch.name}
                                        </option>
                                    ))}
                                </select>
                            ) : null}
                        </Col>
                    </Row>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead style={{ borderBottom: "2px solid #dee2e6" }}>
                            <tr>
                                <th style={tableHeaderStyle}>ID</th>
                                <th style={tableHeaderStyle}>Created At</th>
                                <th style={tableHeaderStyle}>Order ID</th>
                                <th style={tableHeaderStyle}>Merchant</th>
                                <th style={tableHeaderStyle}>Branch</th>
                                <th style={tableHeaderStyle}>Retries</th>
                                <th style={tableHeaderStyle}>Timed Out</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderData?.length > 0 ? (
                                orderData.map((order) => (
                                    <tr
                                        key={order.id}
                                        style={{ borderBottom: "1px solid #dee2e6" }}
                                    >
                                        <td style={tableCellStyle}>{order?.id}</td>
                                        <td style={tableCellStyle}>{formatDate(order.createdAt)}</td>
                                        <td style={tableCellStyle}>
                                            {order.orderId ? (
                                                <span
                                                    style={{ cursor: "pointer", color: "#009689", fontWeight: "bold" }}
                                                    onClick={() => handleOrderClick(order.orderId)}
                                                >
                                                    {order.orderId}
                                                </span>
                                            ) : (
                                                "N/A"
                                            )}
                                        </td>
                                        <td style={tableCellStyle}>{order?.Order?.Merchant?.name}</td>
                                        <td style={tableCellStyle}>{order?.Order?.Branch?.name}</td>
                                        <td style={tableCellStyle}>{order?.retryCount}</td>
                                        <td style={tableCellStyle}>{order?.isTimedOut ? 'Yes' : 'No'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                                        No record found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {orderData?.length > 0 && (
                        <Pagination className="d-flex justify-content-end mt-3">
                            <PaginationItem disabled={currentPage <= 1}>
                                <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                            </PaginationItem>
                            {renderPagination()}
                            <PaginationItem disabled={currentPage >= totalPages}>
                                <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                            </PaginationItem>
                        </Pagination>
                    )}

                    <OrderDetailModal
                        selectedOrder={selectedOrder}
                        orderModalOpen={orderModalOpen}
                        setOrderModalOpen={setOrderModalOpen}
                    />
                </div>
            )}
        </>
    );
};

const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    }) + ', ' + date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true
    });
};

const tableHeaderStyle = {
    padding: "10px",
    textAlign: "left",
    background: "#f2f2f2",
    borderBottom: "1px solid #dee2e6",
};

const tableCellStyle = {
    padding: "10px",
    textAlign: "left",
};

export default DeliveryCancelledOrderTable;
